import { useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  Strategy, X, Plus, SlidersHorizontal, MagnifyingGlass, Trophy, Eye,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/store/appStore';
import { useCatalogs, useTasks } from '@/hooks/useTasks';
import { useEffectiveCapacidad } from '@/hooks/useCapacidades';
import { api } from '@/api/client';
import { ESTADO_IDS } from '@/constants/estadoIds';
import type { Task, EffectiveCapacidad } from '@/types';
import { PlanningCapacidadesModal } from './PlanningCapacidadesModal';

// Tablero de planificación del próximo sprint. Toma toda el área de trabajo
// cuando planningMode está activo. Flujo: pool de Candidatos (tareas que NO
// están en el sprint objetivo) -> se arrastran a la columna de cada persona,
// que las mete al sprint objetivo + asigna responsable; las barras de
// capacidad suben en vivo.
//
// Mecánica de drop (vía PATCH /tasks, no el move del kanban):
//   candidato/otra-persona -> persona  : idSprint=objetivo + responsable=persona
//   -> columna "Sin responsable"        : idSprint=objetivo + responsable=null
//   persona -> Candidatos               : sacar del sprint (idSprint=Sin Asignar)
const SIN_RESP = 'sin' as const;

export function PlanningBoard() {
  const qc = useQueryClient();
  const planningSprintId = useAppStore((s) => s.planningSprintId);
  const setPlanningSprintId = useAppStore((s) => s.setPlanningSprintId);
  const setPlanningMode = useAppStore((s) => s.setPlanningMode);
  const selectTask = useAppStore((s) => s.selectTask);
  const setCreatingTask = useAppStore((s) => s.setCreatingTask);

  // Polling en vivo (10s) solo en planning: si varios planifican a la vez, las
  // tareas y las barras de capacidad se refrescan sin tener que recargar.
  const POLL_MS = 10_000;
  const { data: catalogs } = useCatalogs();
  const { data: tasks = [] } = useTasks('assignee', '', { refetchInterval: POLL_MS });
  const { data: capacidad = [] } = useEffectiveCapacidad(planningSprintId, { refetchInterval: POLL_MS });

  const [search, setSearch] = useState('');
  const [capsOpen, setCapsOpen] = useState(false);

  const users = catalogs?.users ?? [];
  const sprints = catalogs?.sprints ?? [];
  const sinAsignarId = useMemo(
    () => sprints.find((s) => s.tipo === 'SinAsignar')?.idSprint ?? null,
    [sprints],
  );
  const targetSprint = sprints.find((s) => s.idSprint === planningSprintId) ?? null;

  const capByCol = useMemo(() => {
    const m = new Map<number, EffectiveCapacidad>();
    capacidad.forEach((c) => m.set(c.idCol, c));
    return m;
  }, [capacidad]);

  // Resumen del equipo para el sprint objetivo: total asignadas vs disponibles.
  const teamAsignadas = useMemo(() => capacidad.reduce((s, c) => s + c.horasAsignadas, 0), [capacidad]);
  const teamDisponibles = useMemo(() => capacidad.reduce((s, c) => s + c.horasDisponibles, 0), [capacidad]);

  // Candidatas: activas, no completadas, que NO están en el sprint objetivo.
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks
      .filter((t) =>
        t.esActivo !== false
        && t.idEstado !== ESTADO_IDS.COMPLETADO
        && t.idSprint !== planningSprintId)
      .filter((t) => !q || (t.nombreTarea ?? '').toLowerCase().includes(q))
      .sort((a, b) => (a.nombreTarea ?? '').localeCompare(b.nombreTarea ?? '', 'es'));
  }, [tasks, planningSprintId, search]);

  // Tareas ya en el sprint objetivo, agrupadas por responsable.
  const inSprint = useMemo(
    () => tasks.filter((t) => t.esActivo !== false && t.idSprint === planningSprintId),
    [tasks, planningSprintId],
  );
  const byResp = useMemo(() => {
    const m = new Map<number | typeof SIN_RESP, Task[]>();
    inSprint.forEach((t) => {
      const key = t.responsable ?? SIN_RESP;
      const arr = m.get(key) ?? [];
      arr.push(t);
      m.set(key, arr);
    });
    return m;
  }, [inSprint]);

  // Carga como revisor por colaborador en el sprint objetivo: total = todas
  // donde es Autorizado Interno; revisadas = con fecha de autorización.
  const revisorByCol = useMemo(() => {
    const m = new Map<number, { revisadas: number; total: number }>();
    inSprint.forEach((t) => {
      if (t.idAutorizadoInt == null) return;
      const e = m.get(t.idAutorizadoInt) ?? { revisadas: 0, total: 0 };
      e.total++;
      if (t.fechaAutorizadoInt != null) e.revisadas++;
      m.set(t.idAutorizadoInt, e);
    });
    return m;
  }, [inSprint]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  async function applyAssign(taskId: number, patch: { idSprint?: number | null; responsable?: number | null }) {
    // Optimista: parchea la cache de tasks para que la card se mueva al instante.
    qc.setQueriesData<Task[]>({ queryKey: ['tasks'] }, (old) =>
      old?.map((t) => (t.idTareaArq === taskId ? { ...t, ...patch } : t)),
    );
    try {
      await api.tasks.update(taskId, patch);
    } finally {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      if (planningSprintId) {
        qc.invalidateQueries({ queryKey: ['capacidades', 'effective', planningSprintId] });
      }
    }
  }

  function handleDragEnd(e: DragEndEvent) {
    const overId = e.over?.id ? String(e.over.id) : null;
    const activeId = String(e.active.id);
    if (!overId || !activeId.startsWith('task-')) return;
    const taskId = Number(activeId.slice('task-'.length));
    const task = tasks.find((t) => t.idTareaArq === taskId);
    if (!task) return;

    if (overId === 'plan-candidates') {
      // Sacar del sprint objetivo (vuelve a candidatos).
      if (task.idSprint === planningSprintId) {
        applyAssign(taskId, { idSprint: sinAsignarId });
      }
      return;
    }
    if (overId.startsWith('plan-col-')) {
      const colKey = overId.slice('plan-col-'.length);
      const responsable = colKey === SIN_RESP ? null : Number(colKey);
      // No-op si ya está en ese sprint + responsable.
      if (task.idSprint === planningSprintId && (task.responsable ?? null) === responsable) return;
      applyAssign(taskId, { idSprint: planningSprintId, responsable });
    }
  }

  function exitPlanning() {
    setPlanningMode(false);
  }

  return (
    <div className="flex h-full flex-col bg-brand-bg">
      {/* Barra de controles del planning */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3 border-b border-stone-200 bg-white px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-stone-900">
          <Strategy size={18} weight="bold" className="text-brand-green-200" />
          Planificando
        </span>

        <select
          value={planningSprintId ?? ''}
          onChange={(e) => setPlanningSprintId(e.target.value ? Number(e.target.value) : null)}
          title="Sprint objetivo"
          className="cursor-pointer rounded-md border border-stone-300 bg-white px-2 py-1 text-xs font-semibold text-stone-700 focus:outline-none focus:ring-2 focus:ring-brand-green-100 max-w-[260px]"
        >
          {sprints
            .filter((s) => s.tipo !== 'SinAsignar')
            .map((s) => (
              <option key={s.idSprint} value={s.idSprint}>{s.nombre}</option>
            ))}
        </select>

        <button
          type="button"
          onClick={() => setCreatingTask(true)}
          className="flex items-center gap-1.5 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100"
        >
          <Plus size={14} weight="bold" /> Nueva tarea
        </button>

        <button
          type="button"
          onClick={() => setCapsOpen(true)}
          className="flex items-center gap-1.5 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100"
        >
          <SlidersHorizontal size={14} weight="bold" /> Capacidades
        </button>

        {/* Resumen del equipo: total asignadas / disponibles del sprint objetivo. */}
        <TeamSummary asignadas={teamAsignadas} disponibles={teamDisponibles} />

        <button
          type="button"
          onClick={exitPlanning}
          className="ml-auto flex items-center gap-1.5 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-100"
        >
          <X size={14} weight="bold" /> Salir
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={handleDragEnd}>
        <div className="flex flex-1 gap-3 overflow-x-auto p-3">
          {/* Pool de candidatos */}
          <CandidatesColumn
            count={candidates.length}
            totalHoras={candidates.reduce((sum, t) => sum + (t.horasEstimadas ?? 0), 0)}
            search={search}
            onSearch={setSearch}
          >
            {candidates.map((t) => (
              <PlanningCard key={t.idTareaArq} task={t} onOpen={() => selectTask(t.idTareaArq)} />
            ))}
            {candidates.length === 0 && (
              <p className="px-1 py-6 text-center text-xs italic text-stone-400">
                Sin candidatas. Las tareas del sprint actual sin completar y las nuevas aparecen acá.
              </p>
            )}
          </CandidatesColumn>

          {/* Columna "Sin responsable" (tareas en el sprint pero sin asignar) */}
          <PersonColumn
            colKey={SIN_RESP}
            name="Sin responsable"
            tasks={byResp.get(SIN_RESP) ?? []}
            onOpenTask={selectTask}
          />

          {/* Una columna por colaborador del equipo */}
          {users.map((u) => (
            <PersonColumn
              key={u.idCol}
              colKey={u.idCol}
              name={u.nombreCompleto || u.nombre || `#${u.idCol}`}
              tasks={byResp.get(u.idCol) ?? []}
              capacidad={capByCol.get(u.idCol)}
              revisor={revisorByCol.get(u.idCol)}
              onOpenTask={selectTask}
            />
          ))}
        </div>
      </DndContext>

      {capsOpen && targetSprint && (
        <PlanningCapacidadesModal
          sprintId={targetSprint.idSprint}
          sprintNombre={targetSprint.nombre}
          onClose={() => setCapsOpen(false)}
        />
      )}
    </div>
  );
}

// Resumen del equipo en la barra: mini-barra + "asignadas / disponibles".
function TeamSummary({ asignadas, disponibles }: { asignadas: number; disponibles: number }) {
  const ratio = disponibles > 0 ? asignadas / disponibles : (asignadas > 0 ? 1 : 0);
  const fill = ratio >= 1 ? '#BB4A4A' : ratio >= 0.8 ? '#F0C802' : '#ADD010';
  return (
    <div
      className="flex items-center gap-2 rounded-md border border-stone-300 px-2.5 py-1"
      title={`Equipo: ${asignadas.toFixed(1)}h asignadas de ${disponibles.toFixed(1)}h disponibles`}
    >
      <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">
        Equipo
      </span>
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-stone-200">
        <div className="h-full transition-all" style={{ width: `${Math.min(ratio, 1) * 100}%`, background: fill }} />
      </div>
      <span className="whitespace-nowrap font-mono text-xs font-semibold text-stone-700">
        {asignadas.toFixed(1)} / {disponibles.toFixed(0)}h
      </span>
    </div>
  );
}

// ---- Columnas ----

function CandidatesColumn({
  count, totalHoras, search, onSearch, children,
}: {
  count: number;
  totalHoras: number;
  search: string;
  onSearch: (s: string) => void;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: 'plan-candidates' });
  return (
    <div
      ref={setNodeRef}
      className={`flex w-[280px] shrink-0 flex-col rounded-md border border-stone-200 bg-white ${
        isOver ? 'ring-2 ring-brand-green-100 ring-offset-1' : ''
      }`}
    >
      <div className="flex flex-col gap-2 border-b border-stone-200 px-3 py-2.5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-stone-700">Candidatos</span>
          <div className="flex items-center gap-1.5">
            {/* Horas totales de las candidatas (las que tienen estimación). */}
            <span className="rounded-full bg-stone-100 px-2 py-0.5 font-mono text-xs font-medium text-stone-600" title="Horas estimadas totales de las candidatas">
              {totalHoras.toFixed(totalHoras % 1 === 0 ? 0 : 1)}h
            </span>
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500">{count}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded border border-stone-300 px-2 py-1">
          <MagnifyingGlass size={13} weight="bold" className="text-stone-400" />
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Buscar candidata…"
            className="w-full text-xs focus:outline-none"
          />
        </div>
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto p-2">{children}</div>
    </div>
  );
}

function PersonColumn({
  colKey, name, tasks, capacidad, revisor, onOpenTask,
}: {
  colKey: number | typeof SIN_RESP;
  name: string;
  tasks: Task[];
  capacidad?: EffectiveCapacidad;
  revisor?: { revisadas: number; total: number };
  onOpenTask: (id: number) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `plan-col-${colKey}` });
  return (
    <div
      ref={setNodeRef}
      className={`flex w-[260px] shrink-0 flex-col rounded-md bg-stone-100 ${
        isOver ? 'ring-2 ring-brand-green-100 ring-offset-1' : ''
      }`}
    >
      <div className="flex flex-col gap-1.5 border-b border-stone-200 px-3 py-2.5">
        <div className="flex items-center justify-between">
          <span className="truncate text-sm font-semibold text-stone-700" title={name}>{name}</span>
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-stone-500">{tasks.length}</span>
        </div>
        {capacidad && capacidad.horasEfectivas > 0 && (
          <PlanCapacidadBar
            asignadas={capacidad.horasAsignadas}
            disponibles={capacidad.horasDisponibles}
            tieneOverride={capacidad.tieneOverride}
          />
        )}
        {/* Barra de revisión: revisadas / total como Autorizado Interno.
            Verde = revisadas; el vacío = pendientes de revisar. */}
        {revisor && revisor.total > 0 && (
          <PlanRevisorBar revisadas={revisor.revisadas} total={revisor.total} />
        )}
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto p-2 min-h-[120px]">
        {tasks.map((t) => (
          <PlanningCard key={t.idTareaArq} task={t} onOpen={() => onOpenTask(t.idTareaArq)} />
        ))}
        {tasks.length === 0 && (
          <div className="py-8 text-center text-xs italic text-stone-400">Soltá una tarea aquí</div>
        )}
      </div>
    </div>
  );
}

// ---- Card arrastrable ----

function PlanningCard({ task, onOpen }: { task: Task; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `task-${task.idTareaArq}` });
  const horas = task.horasEstimadas;
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onOpen}
      className={`cursor-grab rounded-md border border-stone-200 bg-white px-2.5 py-2 text-left active:cursor-grabbing hover:border-brand-gray-300 ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="mb-1 line-clamp-2 text-xs font-medium text-stone-800">
        {task.nombreTarea || `#${task.idTareaArq}`}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {task.nomEstado && (
          <span
            className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold text-white"
            style={{ background: task.colorEstado ?? '#888780' }}
          >
            {task.nomEstado}
          </span>
        )}
        {task.nomProyecto && (
          <span className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[10px] text-stone-500">
            {task.nomProyecto}
          </span>
        )}
        <span className={`ml-auto flex items-center gap-0.5 font-mono text-[10px] ${horas == null ? 'text-amber-600' : 'text-stone-500'}`}>
          {horas == null ? <><Trophy size={10} weight="bold" /> s/est</> : `${horas}h`}
        </span>
      </div>
    </div>
  );
}

// Barra de capacidad para columnas claras (planning). Verde/amber/rojo según
// ratio asignadas/disponibles.
function PlanCapacidadBar({
  asignadas, disponibles, tieneOverride,
}: {
  asignadas: number;
  disponibles: number;
  tieneOverride: boolean;
}) {
  const ratio = disponibles > 0 ? asignadas / disponibles : (asignadas > 0 ? 1 : 0);
  const clamped = Math.min(ratio, 1);
  const fill = ratio >= 1 ? '#BB4A4A' : ratio >= 0.8 ? '#F0C802' : '#ADD010';
  return (
    <div className="flex items-center gap-2 text-[10px] text-stone-500">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-200"
        title={`${asignadas.toFixed(1)}h de ${disponibles.toFixed(1)}h disponibles${tieneOverride ? ' (override)' : ''}`}>
        <div className="h-full transition-all" style={{ width: `${clamped * 100}%`, background: fill }} />
      </div>
      <span className="whitespace-nowrap font-mono">
        {asignadas.toFixed(1)}/{disponibles.toFixed(0)}h{tieneOverride && <span className="ml-0.5" title="Override de sprint">*</span>}
      </span>
    </div>
  );
}

// Barra de revisión para columnas claras (planning). Verde = revisadas (con
// fecha de autorización); el vacío = pendientes. total = todas como revisor.
function PlanRevisorBar({ revisadas, total }: { revisadas: number; total: number }) {
  const ratio = total > 0 ? revisadas / total : 0;
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-stone-500">
      <Eye size={11} weight="bold" className="shrink-0 text-stone-400" />
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone-200"
        title={`${revisadas} revisadas de ${total} como revisor`}>
        <div className="h-full transition-all" style={{ width: `${ratio * 100}%`, background: '#1D9E75' }} />
      </div>
      <span className="whitespace-nowrap font-mono">{revisadas}/{total} rev</span>
    </div>
  );
}
